import { useState } from 'react';
import {
  eyebrowStyle, h2Style, subtitleStyle, sectionLabelStyle,
  inputStyle, primaryBtnStyle,
} from './tokens';
import { insertExperience, humanizeError } from '../../lib/communityService';

/**
 * ShareExperienceSheet — bottom sheet to add an experience to a question.
 * Wired to Supabase. Passes the new row up via onSent so the parent can prepend
 * it to the list without a refetch.
 */
export default function ShareExperienceSheet({ userId, questionId, onClose, onSent }) {
  const [identity, setIdentity] = useState('anonymous');
  const [text, setText] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const canSend = text.trim().length >= 20 && !sending;

  const submit = async () => {
    if (!canSend) return;
    if (!userId) { setError('You need to be signed in.'); return; }
    setSending(true);
    setError(null);
    const res = await insertExperience({
      userId, questionId,
      anonymous: identity === 'anonymous',
      text: text.trim(),
    });
    setSending(false);
    if (res.error) { setError(humanizeError(res.error)); return; }
    setSent(true);
    onSent?.(res.row);
  };

  return (
    <div
      onClick={onClose}
      data-testid="share-experience-sheet"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(14,15,19,.45)', zIndex: 9998,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 640, background: '#fff',
          borderRadius: '20px 20px 0 0', padding: '20px 20px 32px',
          maxHeight: '85vh', overflowY: 'auto',
          boxShadow: '0 -20px 60px rgba(0,0,0,.24)',
        }}
      >
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'rgba(0,0,0,.15)', margin: '0 auto 16px' }} />
        <div style={eyebrowStyle}>SHARE YOUR EXPERIENCE</div>

        {sent ? (
          <div style={{ padding: 20, textAlign: 'center' }} data-testid="share-experience-sent">
            <div style={{ fontSize: 40, marginBottom: 8 }}>✓</div>
            <h2 style={h2Style}>Thank you for sharing.</h2>
            <p style={subtitleStyle}>Your experience helps someone else feel less alone.</p>
            <button onClick={onClose} style={primaryBtnStyle} data-testid="share-experience-close">Close</button>
          </div>
        ) : (
          <>
            <h2 style={h2Style}>Tell the community.</h2>
            <p style={{ ...subtitleStyle, marginBottom: 16 }}>What happened? What helped you? Anything you&rsquo;d like others to know?</p>

            <div style={sectionLabelStyle}>Identity</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              {[
                { key: 'me', label: 'Me' },
                { key: 'anonymous', label: 'Anonymous' },
              ].map((opt) => (
                <button
                  key={opt.key}
                  onClick={() => setIdentity(opt.key)}
                  data-testid={`share-identity-${opt.key}`}
                  style={{
                    flex: 1, padding: '10px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
                    background: identity === opt.key ? 'rgba(109,94,245,.10)' : '#FFFFFF',
                    border: identity === opt.key ? '1px solid rgba(109,94,245,.30)' : '1px solid rgba(0,0,0,.06)',
                    color: '#232323', fontSize: 13,
                  }}
                >
                  {identity === opt.key ? '● ' : '○ '}{opt.label}
                </button>
              ))}
            </div>

            <div style={sectionLabelStyle}>Experience</div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="share-experience-textarea"
              placeholder="Share your story…"
              style={{ ...inputStyle, minHeight: 140, resize: 'vertical' }}
            />

            <button
              onClick={submit}
              disabled={!canSend}
              data-testid="share-experience-submit"
              style={{ ...primaryBtnStyle, marginTop: 16, opacity: canSend ? 1 : 0.4, cursor: canSend ? 'pointer' : 'not-allowed' }}
            >
              {sending ? 'Sharing…' : 'Share Experience'}
            </button>
            {error && (
              <div data-testid="share-experience-error" style={{ fontSize: 12, color: '#C67A69', textAlign: 'center', marginTop: 10 }}>
                {error}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
